import { nextTick, ref } from 'vue';
import { useLoader, useNotif, useValidation, Validator } from '@orion.ui/orion';
import { Reactive } from '@/utils/decorators';
import BaseSetupService from '@/setup/BaseSetupService';
import axios from 'axios';
import { isEmpty } from 'lodash-es';
import { isOnline } from '@/utils/CONST';
import useSelfService from '@/services/SelfService';
import useFingerprintService from '@/services/FingerprintService';
import useAuthService from '@/services/AuthService';
import OAuthApi from '@/api/oauth/OAuthApi';

type Props = SetupProps<typeof LoginFormSetupService.props>

type News = {
	thumbnail: {
		url: string
	},
	title: string,
	content: string
}

export default class LoginFormSetupService extends BaseSetupService<Props> {
	static readonly props = {};

	@Reactive private readonly state = {
		showLogoAnimation: false,
		showForm: false,
		dataLoaded: true,
		news: null as Nullable<News>,
		loaderMessage: undefined as Undef<string>,
		errorMessage: undefined,
		accountLocked: false,
		needFpValidation: false,
		token: undefined as Undef<string>,
		phoneNumber: undefined as Undef<IUserFingerprintPhoneModel>,
		fpCodeSentByMail: true,
		fpSendingRequest: false,
		showPassword: false,
		needSSO: undefined as Undef<boolean>,
	};

	readonly _email = ref<OrionInput>();
	readonly _password = ref<OrionPassword>();
	readonly _otp = ref<OrionOtp>();

	@Reactive readonly user = {
		username: '',
		password: '',
	};

	readonly validation = useValidation(this.user, {
		username: new Validator([Validator.rules.email(), Validator.rules.required()]),
		password: Validator.rules.required(),
	});

	get showLogoAnimation () { return this.state.showLogoAnimation; }
	get showForm () { return this.state.showForm; }
	get errorMessage () { return this.state.errorMessage; }
	get loaderMessage () { return this.state.loaderMessage; }
	get accountLocked () { return this.state.accountLocked; }
	get needFpValidation () { return this.state.needFpValidation; }
	get phoneNumber () { return this.state.phoneNumber; }
	get fpCodeSentByMail () { return this.state.fpCodeSentByMail; }
	get fpSendingRequest () { return this.state.fpSendingRequest; }
	get showPassword () { return this.state.showPassword; }
	get needSSO () { return this.state.needSSO; }


	protected onMounted () {
		setTimeout(() => this.animate(), 100);
		setTimeout(() => this._email.value?.focus(), 200);
	}

	animate () {
		this.state.showLogoAnimation = true;
		setTimeout(() => this.state.showForm = true, 1000);
	}

	interpretError () {
		if (this.route.query.error) {
			switch (this.route.query.error) {

			// Erreur personalisée de AuthErrorCode.cs
			case 'user_not_found':
				return 'Utilisateur introuvable. Veuillez contacter votre administrateur.';
			case 'access_product_denied':
				return 'Vous n\'avez pas accès à Armado.';
			case 'armado_server_error':
				return 'Erreur du serveur. Veuillez réessayer ultérieurement.';

			// Erreur de configuration
			case 'unauthorized_client':
			case 'invalid_request':
			case 'invalid_scope':
			case 'server_error':
			case 'unsupported_response_type':
				return 'Erreur du serveur. Veuillez réessayer ultérieurement.';

			// Autre erreurs oauth2
			case 'access_denied':
				return 'Accès refusé';
			case 'temporarily_unavailable':
				return 'Le service est temporairement indisponible. Veuillez réessayer ultérieurement.';

			// Authentification requise
			case 'invalid_token':
			case 'login_required':
			case 'interaction_required':
			default:
				return undefined;
			}
		}
	}

	resetLogin () {
		this.state.showPassword = false,
		this.state.needSSO = false;
		this.user.password = '';
		this.validation.resetValidationState();
		nextTick(() => this._email.value?.focus());
	}

	async fetchNewsAsync () {
		this.state.dataLoaded = false;

		const newsUrl = isOnline
			? 'https://armado.fr/myarmado-news'
			: 'https://staging.armado.fr/myarmado-news';

		try {
			const { data } = await axios.get(newsUrl);

			if (!isEmpty(data)) {
				this.state.news = data;
			}
		} finally {
			this.state.dataLoaded = true;
		}
	}

	async loginAsync () {
		if (this.needSSO) {
			localStorage.setItem('userType', '1');
			OAuthApi.redirectToIdp({ login_hint: this.user.username });
			return;
		}

		if (this.validation.validate()) {
			const res = await useSelfService().loginAsync({
				username: this.user.username,
				password: this.user.password,
			});

			if (res === 'Compte bloqué') {
				this.state.accountLocked = true;
				return;
			}

			if (res?.message === 'Valider la connexion') {
				this.state.needFpValidation = true;
				this.state.token = res?.code;
				this.state.phoneNumber = undefined;

				if (this.state.token) {
					const getPhoneResult = await useFingerprintService().getPhoneNumberAsync(this.state.token);

					if (getPhoneResult) {
						this.state.phoneNumber = getPhoneResult;
					}
				}
			}

			this.state.errorMessage = res?.message;
		} else {
			this.validation.showValidationState();
		}
	}

	async checkSSOAsync () {
		if (!this._email.value?.isValid()) {
			useNotif.danger('Email non valide');
			return;
		}

		useLoader().show();
		const isSuiteUser = await useAuthService().checkLoginForSuiteAsync({ email: this.user.username });
		useLoader().hide();

		if (isSuiteUser) {
			this.state.needSSO = true;
		} else {
			this.state.needSSO = false;
		}

		this.state.showPassword = true;
		this.validation.resetValidationState();
		nextTick(() => this._password.value?.focus());
	}

	async checkCodeAsync (code: string) {
		if (code?.length < 4 || !this.state.token) return;

		this.state.loaderMessage = `Vérification du code...`;
		try {
			await useFingerprintService().validateAsync(this.state.token, code);
			await this.loginAsync();
		} catch (e: any) {
			this._otp.value?.reset();

			if (e.response?.data?.code === 'fingerprint_trials_exeeded') {
				this.state.needFpValidation = false;
				this.state.token = undefined;
				this.state.phoneNumber = undefined;
				this.user.password = '';
				this.state.errorMessage = undefined;
			}
		} finally {
			this.state.loaderMessage = undefined;
		}
	}

	async resendCodeAsync () {
		if (!this.state.token) return;

		try {
			this.state.fpCodeSentByMail = true;
			this.state.fpSendingRequest = true;
			await useFingerprintService().resendCodeAsync(this.state.token);
			useNotif.info(`Un nouveau code vous a été envoyé par email`);
		} catch (e: any) {
			useNotif.danger(e?.response?.data);
		} finally {
			this.state.fpSendingRequest = false;
		}
	}

	async resendSmsCodeAsync () {
		if (!this.state.token) return;

		try {
			this.state.fpCodeSentByMail = false;
			this.state.fpSendingRequest = true;
			await useFingerprintService().resendSmsCodeAsync(this.state.token);
			useNotif.info(`Un nouveau code vous a été envoyé par sms`);
		} catch (e: any) {
			useNotif.danger(e?.response?.data);
		} finally {
			this.state.fpSendingRequest = false;
		}
	}
}
